/**
 * Control groups ("slots" 1-9): the player's own shortcut lists of units,
 * client-local and apart from formations (sim groups, G / U). Ctrl+N stores
 * the selection in slot N, N recalls it. An empty slot's digit falls back to
 * formation Group N (`resolveDigit`), so the keys keep their old meaning for
 * players who only use G. Ids are kept per match in localStorage
 * (`slotsKey`); dead or foreign ids drop out when read (`liveSlot`). A slot's
 * name lives in the names map under `slotNameKey`. Pure (no React, no storage).
 */

import type { RuleSet } from '../rules';
import { unitGlyph, unitTypeOrder } from '../rules/ui';
import {
  type EntityId,
  fromMilli,
  type PlayerId,
  type SimState,
  type Unit,
  unitMaxHp,
} from '../sim';
import type { OwnGroup } from './groups';

export const SLOT_COUNT = 9;

/** Unit ids by slot number (1-9); a missing slot is empty. */
export type Slots = Readonly<Partial<Record<number, readonly EntityId[]>>>;

/** No slots: the same object every time, so "unchanged" compares by identity. */
export const NO_SLOTS: Slots = {};

/** The key of slot `n`'s name in the names map (view/names.ts). */
export function slotNameKey(n: number): string {
  return `slot:${n}`;
}

/** The localStorage key for one player's slots in one match. */
export function slotsKey(seed: string, playerId: string): string {
  return `territile.slots.${seed}.${playerId}`;
}

/** The localStorage key of the list of stored slot sets, newest last (pruned like the names', `touchNamesIndex`). */
export const SLOTS_INDEX_KEY = 'territile.slots.index';

/** Slots read back from storage (`raw` is the stored text or null); anything malformed is dropped. */
export function parseSlots(raw: string | null): Slots {
  if (raw === null) return NO_SLOTS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return NO_SLOTS;
    const out: Record<number, readonly string[]> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const n = Number(key);
      if (!Number.isInteger(n) || n < 1 || n > SLOT_COUNT || !Array.isArray(value)) continue;
      const ids = [...new Set(value.filter((v): v is string => typeof v === 'string'))];
      if (ids.length > 0) out[n] = ids;
    }
    return Object.keys(out).length > 0 ? out : NO_SLOTS;
  } catch {
    return NO_SLOTS;
  }
}

/** The player's own, existing units among `ids` (selection order kept). */
export function ownUnitsOf(state: SimState, playerId: PlayerId, ids: readonly string[]): Unit[] {
  return ids.flatMap((id) => {
    const u = state.units[id];
    return u?.owner === playerId ? [u] : [];
  });
}

/** Slot `n`'s units that are still ours and alive. */
export function liveSlot(state: SimState, playerId: PlayerId, slots: Slots, n: number): Unit[] {
  return ownUnitsOf(state, playerId, slots[n] ?? []);
}

/**
 * `slots` with slot `n` replaced by the selection's own units. A selection
 * with none leaves the slots alone (Ctrl+N with nothing selected is not a
 * way to clear one). Returns `slots` itself when nothing changes.
 */
export function assignSlot(
  slots: Slots,
  n: number,
  state: SimState,
  playerId: PlayerId,
  selected: readonly string[]
): Slots {
  if (n < 1 || n > SLOT_COUNT) return slots;
  const ids = ownUnitsOf(state, playerId, selected).map((u) => u.id);
  if (ids.length === 0) return slots;
  const old = slots[n];
  if (old && old.length === ids.length && old.every((id, i) => id === ids[i])) return slots;
  return { ...slots, [n]: ids };
}

/** What a digit key acts on: the slot's units, a formation group, or nothing. */
export type DigitTarget =
  | { kind: 'slot'; units: Unit[] }
  | { kind: 'group'; group: OwnGroup }
  | null;

/** Slot `n` when it has live units, else formation Group `n` (the fallback), else nothing. */
export function resolveDigit(
  state: SimState,
  playerId: PlayerId,
  slots: Slots,
  groups: readonly OwnGroup[],
  n: number
): DigitTarget {
  const units = liveSlot(state, playerId, slots, n);
  if (units.length > 0) return { kind: 'slot', units };
  const group = groups.find((g) => g.number === n);
  return group ? { kind: 'group', group } : null;
}

/** Where the camera goes for a slot: its first unit's tile. */
export function slotFocus(units: readonly Unit[]): Unit['pos'] | null {
  return units[0]?.pos ?? null;
}

/** Whether `selected` is exactly `units` (any order). */
export function isSlotSelection(units: readonly Unit[], selected: readonly string[]): boolean {
  return units.length === selected.length && units.every((u) => selected.includes(u.id));
}

/** One unit type in a slot: its roster tag and head count. */
export interface SlotType {
  type: string;
  glyph: string;
  count: number;
}

/** A slot's units by type in roster order ("Wr ×4, He ×1"). */
export function slotComposition(rules: RuleSet, units: readonly Unit[]): SlotType[] {
  return unitTypeOrder(rules).flatMap((type) => {
    const count = units.filter((u) => u.type === type).length;
    return count > 0 ? [{ type, glyph: unitGlyph(rules, type), count }] : [];
  });
}

/** Combined hp of `units` and their maximum, in whole hp. */
export function slotHp(rules: RuleSet, units: readonly Unit[]): { hp: number; max: number } {
  let hp = 0;
  let max = 0;
  for (const u of units) {
    hp += u.hp;
    max += unitMaxHp(rules, u);
  }
  return { hp: Math.ceil(fromMilli(hp)), max: Math.ceil(fromMilli(max)) };
}

export type SlotStatus = 'fighting' | 'moving' | 'idle';

/** Fighting when any unit attacks or is attacked (`engaged`: ids in an engagement), else moving when any walks, else idle. */
export function slotStatus(units: readonly Unit[], engaged: ReadonlySet<EntityId>): SlotStatus {
  if (units.some((u) => engaged.has(u.id))) return 'fighting';
  if (units.some((u) => (u.path?.length ?? 0) > 0)) return 'moving';
  return 'idle';
}

/** Every id in an engagement (attackers and their targets), from `engagements`. */
export function engagedIds(engagements: ReadonlyMap<EntityId, EntityId>): Set<EntityId> {
  const ids = new Set<EntityId>();
  for (const [attacker, target] of engagements) {
    ids.add(attacker);
    ids.add(target);
  }
  return ids;
}

/** The lowest slot holding exactly `selected` (its live units, any order), for F2 on a selected slot. */
export function slotForSelection(
  state: SimState,
  playerId: PlayerId,
  slots: Slots,
  selected: readonly string[]
): number | null {
  if (selected.length === 0) return null;
  for (let n = 1; n <= SLOT_COUNT; n++) {
    if (isSlotSelection(liveSlot(state, playerId, slots, n), selected)) return n;
  }
  return null;
}
